import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, AlertCircle } from "lucide-react";
import { apiClient } from "../../../../api";
import { baseUrl } from "../../../../Environments/environment";
import { useProperty } from "../../../../Contexts/PropertyContext/PropertyContext";
import { toast,ToastContainer } from 'react-toastify';

const PlatformLinking = () => {
  const { selectedPropertyId } = useProperty();
  const [platforms, setPlatforms] = useState([
    {
      name: "Google",
      isLinked: false,
      description: "Connect with Google Business Profile",
      isReady: true,
    },
    {
      name: "TripAdvisor",
      isLinked: false,
      description: "Connect with TripAdvisor",
      isReady: false,
    },
    {
      name: "Airbnb",
      isLinked: false,
      description: "Coming soon",
      isReady: false,
    },
    {
      name: "Booking.com",
      isLinked: false,
      description: "Coming soon",
      isReady: false,
    },
    {
      name: "Expedia",
      isLinked: false,
      description: "Coming soon",
      isReady: false
    },
  ]);

  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Check for existing tokens when component mounts
  useEffect(() => {
    const checkExistingTokens = async () => {
      try {
        const response = await apiClient.get(
          `${baseUrl}/propertyToken/${selectedPropertyId}`
        );
        console.log("response", response);
        if (response.data.statusCode === 200) {
          // Check if there's a Google token
          const hasGoogleToken = response.data.result.some(
            token => token.type === "google" && token.accessToken
          );
          console.log("hasGoogleToken", hasGoogleToken);

          // Update only Google's connection status
          setPlatforms(prev =>
            prev.map(platform =>
              platform.name === "Google"
                ? { ...platform, isLinked: hasGoogleToken }
                : platform
            )
          );
        }
      } catch (error) {
        console.error("Error checking existing tokens:", error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    if (selectedPropertyId) {
      checkExistingTokens();
    }
  }, [selectedPropertyId]);

  const handleGoogleAuth = async () => {
    try {
      setLoading("Google");
      setError(null);

      // Get Google OAuth URL
      const response = await apiClient.get(
        `${baseUrl}/googleAuth/${selectedPropertyId}/auth/google`
      );
      console.log(response.data.result.authUrl);
      if (response.data.result.authUrl) {
        console.log("authUrl", response.data.result.authUrl);
        // Open Google OAuth in a popup window
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        const popup = window.open(
          response.data.result.authUrl,
          "GoogleAuth",
          `width=${width},height=${height},left=${left},top=${top}`
        );

        // Create message event listener for the callback
        const handleMessage = async (event) => {
          // Verify the origin of the message
          const allowedOrigins = [window.location.origin, "https://hotel.onelyk.com"];
          if (!allowedOrigins.includes(event.origin)) return;

          try {
            if (event.data.type === 'google-oauth-callback') {
              const { code, state } = event.data;
              
              // Parse the state to get propertyId
              let propertyId;
              try {
                const stateObj = JSON.parse(decodeURIComponent(state));
                propertyId = stateObj.propertyId;
              } catch (e) {
                console.error('Error parsing state:', e);
                propertyId = selectedPropertyId;
              }

              const tokenResponse = await apiClient.get(
                `${baseUrl}/googleAuth/auth/google/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`
              );
              console.log("tokenResponse", tokenResponse);

              if (tokenResponse.data.success) {
                // Check if connection was successful by calling the propertyToken endpoint
                const tokenCheck = await apiClient.get(
                  `${baseUrl}/propertyToken/${propertyId}`
                );
                
                if (tokenCheck.data.success && tokenCheck.data.result.some(token => token.type === 'google' && token.accessToken)) {
                  // Update only Google's connection status
                  setPlatforms(prev =>
                    prev.map(platform =>
                      platform.name === "Google"
                        ? { ...platform, isLinked: true }
                        : platform
                    )
                  );
                  toast.success("Successfully connected with Google");
                }
              }

              // Close the popup
              if (popup) {
                popup.close();
              }
            }
          } catch (error) {
            console.error("Error handling OAuth callback:", error);
            setError("Failed to complete Google authentication");
            toast.error("Failed to connect with Google");
          }
        };

        // Add message listener
        window.addEventListener("message", handleMessage);

        // Poll for popup closure and cleanup
        const pollTimer = setInterval(() => {
          if (popup.closed) {
            clearInterval(pollTimer);
            window.removeEventListener("message", handleMessage);
          }
        }, 500);
      }
    } catch (error) {
      console.error("Error initiating Google auth:", error);
      setError("Failed to connect with Google");
      toast.error("Failed to connect with Google");
    } finally {
      setLoading(null);
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      setLoading("Google");
      setError(null);

      await apiClient.delete(
        `${baseUrl}/googleAuth/${selectedPropertyId}/google`
      );

      setPlatforms(prev =>
        prev.map(platform =>
          platform.name === "Google"
            ? { ...platform, isLinked: false }
            : platform
        )
      );

      toast.success("Successfully disconnected from Google");
    } catch (error) {
      console.error("Error disconnecting Google:", error);
      setError("Failed to disconnect from Google");
      toast.error("Failed to disconnect from Google");
    } finally {
      setLoading(null);
    }
  };

  const checkConnectionStatus = async () => {
    try {
      const response = await apiClient.get(
        `${baseUrl}/googleAuth/${selectedPropertyId}/google/locations`
      );
      
      if (response.data.success) {
        setPlatforms(prev =>
          prev.map(platform =>
            platform.name === "Google"
              ? { ...platform, isLinked: true }
              : platform
          )
        );
        toast.success("Successfully connected with Google");
      }
    } catch (error) {
      console.error("Error checking connection status:", error);
      setError("Failed to verify connection");
      toast.error("Failed to verify connection");
    }
  };

  const handleToggle = async (platformName) => {
    if (platformName === "Google") {
      if (platforms.find(p => p.name === "Google").isLinked === false) {
        await handleGoogleAuth();
      }
    } else if (platformName === "TripAdvisor") {
      toast.info("Trip Advisor Intergartion");
    } else {
      toast.info("This platform integration is coming soon!");
    }
  };

  // Loading skeleton component
  const PlatformSkeleton = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden animate-pulse">
      <div className="p-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
          <div>
            <div className="h-5 w-32 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 w-48 bg-gray-200 rounded"></div>
          </div>
        </div>
        <div className="w-24 h-10 bg-gray-200 rounded-lg"></div>
      </div>
      <div className="h-1 bg-gray-200"></div>
    </div>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Platform Linking</h1>
          <p className="mt-2 text-sm text-gray-600">
            Connect your property with various booking platforms
          </p>
        </div>

        <div className="space-y-4">
          {isInitialLoading ? (
            // Show skeletons while loading
            <>
              <PlatformSkeleton />
              <PlatformSkeleton />
              <PlatformSkeleton />
              <PlatformSkeleton />
            </>
          ) : (
            // Show actual platforms once loaded
            platforms.map((platform, index) => (
              <motion.div
                key={platform.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden"
              >
                <motion.div
                  className="p-6 flex items-center justify-between"
                  whileHover={{ backgroundColor: "rgba(249, 250, 251, 0.5)" }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-center space-x-4">
                    <motion.div
                      className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span className="text-xl font-semibold text-indigo-600">
                        {platform.name.charAt(0)}
                      </span>
                    </motion.div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {platform.name}
                      </h3>
                      <motion.p 
                        key={platform.isLinked ? "connected" : "disconnected"}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm text-gray-500"
                      >
                        {platform.description}
                      </motion.p>
                      {error && platform.isReady === true && (
                        <motion.p
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="text-sm text-red-500 flex items-center mt-1"
                        >
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {error}
                        </motion.p>
                      )}
                    </div>
                  </div>

                  <motion.button
                    onClick={() => handleToggle(platform.name)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={loading === platform.name || platform.isLinked || (platform.isReady !== true)}
                    className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                      platform.isReady !== true
                        ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                        : loading === platform.name
                        ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                        : platform.isLinked
                        ? "bg-green-100 text-green-700 hover:bg-green-200 cursor-not-allowed opacity-75"
                        : "bg-indigo-600 text-white hover:bg-indigo-700"
                    }`}
                  >
                    {loading === platform.name ? (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex items-center"
                      >
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        <span>Processing...</span>
                      </motion.div>
                    ) : (
                      <>
                        <span className="flex items-center">
                          {platform.isReady === true
                            ? platform.isLinked
                              ? "Connected"
                              : "Connect"
                            : "Coming Soon"}
                        </span>
                        <AnimatePresence mode="wait">
                          {platform.isLinked && (
                            <motion.div
                              key="check"
                              initial={{ scale: 0, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              exit={{ scale: 0, opacity: 0 }}
                              transition={{
                                type: "spring",
                                stiffness: 500,
                                damping: 30,
                              }}
                              className="ml-2"
                            >
                              <Check className="w-4 h-4" />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    )}
                  </motion.button>
                </motion.div>

                {/* Animated border bottom */}
                <motion.div
                  className="h-1 bg-gradient-to-r from-indigo-500 to-indigo-600"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: platform.isLinked ? 1 : 0 }}
                  transition={{ duration: 0.5 }}
                  style={{ transformOrigin: "left" }}
                />
              </motion.div>
            ))
          )}
        </div>
      </div>
      <ToastContainer position="top-right" />
    </div>
  );
};

export default PlatformLinking; 